import { CommunityNotification } from '../services/models/community-notifications.models';
import { NotificationType, ReactionType } from '../services/models/community-enums';

/** Ten rather than NOTIFICATION_LIMITS.defaultTake: the panel is a peek and the page is the list. */
export const PANEL_TAKE = 10;

/** Above this the pill would widen the 41px bar. The true figure stays in the accessible name. */
export const BADGE_CAP = 99;

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const TYPE_KEY_PREFIX = 'knowledge.notifications.types.';
const REACTION_KEY_PREFIX = 'knowledge.notifications.reactions.';
const RELATIVE_KEY_PREFIX = 'knowledge.notifications.relative.';

/** A release number, never a guid. ReleaseDetailComponent does Number(), and NaN renders a blank page. */
const RELEASE_NUMBER = /^[1-9]\d*$/;

/** A Record rather than a lookup, so a seventh enum member is a compile error and not a silent fallback. */
const TYPE_ICONS: Readonly<Record<NotificationType, string>> = {
  [NotificationType.DiscussionReply]: 'reply',
  [NotificationType.DiscussionReactionPost]: 'add_reaction',
  [NotificationType.DiscussionReactionReply]: 'add_reaction',
  [NotificationType.FeatureRequestVote]: 'how_to_vote',
  [NotificationType.FeatureRequestComment]: 'comment',
  [NotificationType.ReleasePublished]: 'new_releases',
};

/** On the two reaction types the glyph carries the reaction, so the sentence does not have to. */
const REACTION_ICONS: Readonly<Record<ReactionType, string>> = {
  [ReactionType.ThumbUp]: 'thumb_up',
  [ReactionType.ThumbDown]: 'thumb_down',
  [ReactionType.Heart]: 'favorite',
  [ReactionType.Celebration]: 'celebration',
};

const UNKNOWN_TYPE_ICON = 'notifications';
const UNKNOWN_TYPE_KEY = `${TYPE_KEY_PREFIX}unknown`;

const KNOWN_TYPES = new Set<string>(Object.values(NotificationType));
const KNOWN_REACTIONS = new Set<string>(Object.values(ReactionType));

export interface RelativeTime {
  /** Null when the age is past the last bucket, and the caller renders an absolute date instead. */
  key: string | null;
  params: { count: number } | null;
}

export interface NotificationRow {
  item: CommunityNotification;
  /** Null when this build cannot address the target, which keeps a future type from linking nowhere. */
  link: readonly string[] | null;
  icon: string;
  textKey: string;
  textParams: { name: string };
  /** Null when the event is not a reaction. Rendered as its own span, never nested in the sentence. */
  reactionKey: string | null;
  time: RelativeTime;
  dataTest: string;
}

function knownType(type: string): NotificationType | null {
  return KNOWN_TYPES.has(type) ? (type as NotificationType) : null;
}

function knownReaction(reactionType: string | null): ReactionType | null {
  return reactionType && KNOWN_REACTIONS.has(reactionType) ? (reactionType as ReactionType) : null;
}

function thread(id: string | null): readonly string[] | null {
  return id ? ['/knowledge/discussions', id] : null;
}

export function notificationDestination(item: CommunityNotification): readonly string[] | null {
  switch (knownType(item.type)) {
    case NotificationType.DiscussionReply:
    case NotificationType.DiscussionReactionPost:
      return thread(item.targetId);

    // targetId is the reply, which has no route of its own. parentId is the thread that holds it.
    case NotificationType.DiscussionReactionReply:
      return thread(item.parentId);

    case NotificationType.FeatureRequestVote:
    case NotificationType.FeatureRequestComment:
      return item.targetId ? ['/knowledge/feature-requests', item.targetId] : null;

    case NotificationType.ReleasePublished:
      return RELEASE_NUMBER.test(item.targetId) ? ['/knowledge/release-notes', item.targetId] : null;

    default:
      return null;
  }
}

/**
 * Three buckets, then the caller falls back to a date. ngx-translate has no plural compiler here, so a days
 * bucket would need "1 dag" and "2 dagar" as separate keys, while min and h are invariant in both languages.
 * `now` is a parameter so the function stays pure and every boundary is pinnable.
 */
export function relativeTime(createdAt: string, now: number): RelativeTime {
  const age = now - new Date(createdAt).getTime();
  if (!Number.isFinite(age) || age < MINUTE_MS) {
    return { key: `${RELATIVE_KEY_PREFIX}now`, params: null };
  }
  if (age < HOUR_MS) {
    return { key: `${RELATIVE_KEY_PREFIX}minutes`, params: { count: Math.floor(age / MINUTE_MS) } };
  }
  if (age < DAY_MS) {
    return { key: `${RELATIVE_KEY_PREFIX}hours`, params: { count: Math.floor(age / HOUR_MS) } };
  }
  return { key: null, params: null };
}

export function notificationRow(item: CommunityNotification, now: number): NotificationRow {
  const type = knownType(item.type);
  const reaction = knownReaction(item.reactionType);

  return {
    item,
    link: notificationDestination(item),
    icon: reaction ? REACTION_ICONS[reaction] : type ? TYPE_ICONS[type] : UNKNOWN_TYPE_ICON,
    textKey: type ? `${TYPE_KEY_PREFIX}${type}` : UNKNOWN_TYPE_KEY,
    textParams: { name: item.actorName ?? '' },
    reactionKey: reaction ? `${REACTION_KEY_PREFIX}${reaction}` : null,
    time: relativeTime(item.createdAt, now),
    dataTest: `notification-row-${item.id}`,
  };
}

export function badgeText(unreadCount: number): string {
  return unreadCount > BADGE_CAP ? `${BADGE_CAP}+` : `${unreadCount}`;
}
